import React from "react";
import {connect} from "react-redux";
import {updatePage} from "../../actions/page";
import {Link} from "react-router-dom";
import {Card, Col, ConfigProvider, message, Popconfirm, Result, Row, Table, Tooltip} from "antd";
import Button from "antd/es/button";
import {createDomainGroup, deleteDomainGroup, loadDomainGroups} from "../../actions/domains";
import {displayErrors} from "../../libs/utils";
import {DateFormat} from "../../components/shared/DateFormat";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import UndoOutlined from "@ant-design/icons/lib/icons/UndoOutlined";
import AddDomainGroupModal from "../../components/domain/AddDomainGroupModal";
import {ClusterOutlined, DeleteOutlined, FolderOpenOutlined} from "@ant-design/icons";
import AccessGate from "../../components/permissions/AccessGate";
import AccessItem from "../../components/permissions/AccessItem";
import {hasPermission} from "../../components/permissions/PermissionUtils";
import {disableDriver} from "../../actions/driver";

class Domains extends React.Component {
    state = {
        filters: null,
        groupsLoaded: false,
        groups: [],
        showAddModal: false,
        newGroupName: '',
        creatingNewGroup: false,
        pagination: { current: 1, pageSize: 10, showSizeChanger: true, total: 0 }
    };

    componentDidMount() {
        if(this.props.user.panel_mode === 'SIMPLE') {
            const domainGroup = this.props.user.domain_groups.find(item => item.is_internal);
            return this.props.history.push('/domains/' + domainGroup.guid);
        }


        this.props.updatePage('Domain Groups');
        this.loadGroups();
    }

    loadGroups(page = null, per_page = null) {
        if(page == null) {
            page = this.state.pagination.current;
            per_page = this.state.pagination.pageSize;
        }

        this.setState({ groupsLoaded: false });

        this.props.loadDomainGroups(page, per_page, this.state.filters, (response) => {
            this.setState({ groups: response.data.data,
                pagination: { current: response.data.page, pageSize: response.data.per_page, showSizeChanger: true, total: response.data.total }, groupsLoaded: true });
        }, (err) => {
            this.setState({ groupsLoaded: true});

            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
            }
        });
    }

    deleteGroup(guid) {
        this.setState({ groupsLoaded: false });

        this.props.deleteDomainGroup(guid, () => {
            message.success('Domain group successfully deleted!');
            this.loadGroups();
        }, (err) => {
            this.setState({ groupsLoaded: true});

            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
            }
        });
    }

    showAddDomainGroupModal() {
        this.setState({ showAddModal: true });
        
        this.props.disableDriver();
    }

    render() {
        const columns = [
            { title: 'Name', dataIndex: 'name', key: 'name', render: (item, record) => <strong><Link to={'/domains/' + record.guid}>{record.name}</Link></strong> },
            {title: 'Domains', dataIndex: 'domain_count', align: 'center', key: 'domain_count'},
            { title: 'Created', dataIndex: 'date_created', align: 'center', key: 'date_created', render: (item, record) => <DateFormat value={record.date_created} /> },
        ];

        if(hasPermission(this.props.company.permissions, ['can_delete_ip_groups'])) {
            columns.push({ title: '', align: 'right', render: (item, record) => (
                    <Popconfirm
                        title="Are you sure you want to delete this group?"
                        onConfirm={() => this.deleteGroup(record.guid)}
                        okText='Delete'
                        placement='topRight'
                        disabled={record.is_internal || record.domain_count > 0}
                        cancelText='Cancel'>
                        <Tooltip title='Delete domain group' placement='left'>
                            <Button icon={<DeleteOutlined />} type='text' style={{color: 'lightgray'}}  size='small' disabled={record.is_internal || record.domain_count > 0} />
                        </Tooltip>
                    </Popconfirm>
                )});
        }

        if(this.props.company.ip_address_count === 0) {
            let internalIPGroup = this.props.user.ip_address_groups.filter(group => group.is_internal);
            let ipsUrl;

            if(internalIPGroup.length > 0) {
                ipsUrl = '/ip-addresses/' + internalIPGroup[0].guid + '?show-add-modal';
            } else {
                ipsUrl = '/ip-addresses?show-add-ip-group-modal';
            }

            return <AccessGate scopes={['can_view_domain_groups']}>
                <Card>
                    <Result
                        icon={<ClusterOutlined />}
                        title="IP Addresses Required!"
                        status='info'
                        subTitle={<div style={{color: '#494949'}}>To get started with adding domains, you will first need to add IP addresses to your account.<br />Simply add IP addresses using the button below, and you will be ready to connect your domains in just a moment.</div>}
                        extra={<Link to={ipsUrl}><Button type="primary">Add IP Addresses</Button></Link>}
                    />
                </Card>
            </AccessGate>;
        }

        return(
            <AccessGate scopes={['can_view_domain_groups']}>
                <Card size='small'>
                    <Row justify='space-between' style={{paddingBottom: '10px'}}>
                        <Col>
                            <AccessItem scopes={['can_add_domain_groups']}>
                                <Button className='add-domain-group-btn' icon={<PlusOutlined />} onClick={() => this.showAddDomainGroupModal()}>New Domain Group</Button>
                            </AccessItem>
                        </Col>
                        <Col><Tooltip title='Reload table'><Button type='text' icon={<UndoOutlined />} onClick={() => this.loadGroups()}/></Tooltip></Col>
                    </Row>

                    <ConfigProvider renderEmpty={() => <div style={{textAlign: 'center'}}>
                        <FolderOpenOutlined style={{fontSize: 40}}/>
                        <p>Nothing to see here...</p>
                        <AccessItem scopes={['can_add_domain_groups']}>
                            <Button icon={<PlusOutlined/>} onClick={() => this.setState({showAddModal: true})}>Add
                                Domain Group</Button>
                        </AccessItem>
                    </div>}>
                        <div style={{overflowX: 'auto'}}>
                            <Table
                                dataSource={this.state.groups}
                                columns={columns}
                                rowKey={(item) => item.guid}
                                pagination={this.state.pagination}
                                onChange={(pagination) => this.loadGroups(pagination.current, pagination.pageSize)}
                                loading={!this.state.groupsLoaded}/>
                        </div>
                    </ConfigProvider>
                </Card>

                <AddDomainGroupModal
                    reloadGroups={() => this.loadGroups()}
                    showModal={this.state.showAddModal}
                    history={this.props.history}
                    close={() => this.setState({ showAddModal: false })} />
            </AccessGate>
        );
    }
}

const mapStateToProps = state => ({
    user: state.auth.user.user,
    company: state.auth.user.user.company
});

export default connect(mapStateToProps, { updatePage, loadDomainGroups, createDomainGroup,
    deleteDomainGroup, disableDriver })(Domains);